import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  format, parseISO, startOfWeek, endOfWeek, addDays, eachHourOfInterval, 
  isToday, getHours, isSameDay, formatDistance, isBefore, isAfter, addHours, 
  differenceInMinutes, eachDayOfInterval, eachMinuteOfInterval, isSameMonth, startOfDay,
  areIntervalsOverlapping, addMinutes, startOfMonth, endOfMonth,
  differenceInCalendarDays, getDay, getMinutes
} from 'date-fns';
import { setSelectedDate, setLoadingShared, setSharedEvents, setSharedEventsError } from '../state';
import { fetchShareRequests, setReminder, shareEvent, fetchEvents, fetchWeekEvents, updateEvent, deleteEvent } from '../../../state/calendarSlice';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon, CalendarIcon, ClockIcon, MapPinIcon, ChevronDoubleRightIcon, ChevronDoubleLeftIcon, PencilIcon, TrashIcon, BellIcon, ShareIcon, PlusCircleIcon } from '@heroicons/react/24/outline';
import { Bars3Icon as MenuIcon } from '@heroicons/react/24/outline';
import { FaClock, FaMapMarkerAlt, FaInfoCircle, FaPencilAlt, FaTrash, FaBell, FaShare, FaUserFriends, FaCheck, FaTimes } from 'react-icons/fa';
import NewEventForm from '../components/NewEventForm';
import EventDetailsDialog from './EventDetailsDialog';
import calendarService from '../../../services/calendarService';
import ShareEventDialog from './ShareEventDialog';

const eventColors = ['#FBBC04', '#4CCEAC', '#A0AEC0', '#FF0000', '#FFFFFF'];

const groupOverlappingEvents = (events) => {
  if (!events || events.length === 0) return [];
  
  const sortedEvents = [...events].sort((a, b) => 
    parseISO(a.startTime || a.start).getTime() - parseISO(b.startTime || b.start).getTime()
  );

  const groups = [];
  let currentGroup = [];

  sortedEvents.forEach(event => {
    if (currentGroup.length === 0) {
      currentGroup.push(event);
    } else {
      const lastEvent = currentGroup[currentGroup.length - 1];
      const eventStart = event.startTime || event.start;
      const eventEnd = event.endTime || event.end;
      const lastEventStart = lastEvent.startTime || lastEvent.start;
      const lastEventEnd = lastEvent.endTime || lastEvent.end;
      
      if (areIntervalsOverlapping(
        { start: parseISO(eventStart), end: parseISO(eventEnd) },
        { start: parseISO(lastEventStart), end: parseISO(lastEventEnd) }
      )) {
        currentGroup.push(event);
      } else {
        groups.push([...currentGroup]);
        currentGroup = [event];
      }
    }
  });

  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
};

const DeleteConfirmDialog = ({ onConfirm, onCancel }) => (
  <motion.div
    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onCancel}
  >
    <motion.div
      className="bg-gradient-to-br from-sec-dark to-pri-dark p-6 rounded-2xl max-w-sm w-full mx-4 border border-pri-white/10"
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      onClick={e => e.stopPropagation()}
    >
      <h3 className="text-xl font-bold text-pri-yellow mb-4">Delete Event</h3>
      <p className="text-pri-white/70 mb-6">Are you sure you want to delete this event? This action cannot be undone.</p>
      <div className="flex justify-end gap-3">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onCancel}
          className="px-4 py-2 rounded-xl bg-pri-white/10 text-pri-white hover:bg-pri-white/20"
        >
          Cancel
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onConfirm}
          className="px-4 py-2 rounded-xl bg-red-500/10 text-red-500 hover:bg-red-500/20"
        >
          Delete
        </motion.button>
      </div>
    </motion.div>
  </motion.div>
);

const ShareMenu = ({ event, onClose, onShare }) => (
  <motion.div
    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onClose}
  >
    <motion.div
      className="bg-gradient-to-br from-sec-dark to-pri-dark p-6 rounded-2xl max-w-sm w-full mx-4 border border-pri-white/10"
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      onClick={e => e.stopPropagation()}
    >
      <h3 className="text-xl font-bold text-pri-yellow mb-4">Share Event</h3>
      {/* Add share options here */}
      <div className="flex justify-end mt-4">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onClose}
          className="px-4 py-2 rounded-xl bg-pri-white/10 text-pri-white hover:bg-pri-white/20"
        >
          Close
        </motion.button>
      </div>
    </motion.div>
  </motion.div>
);

const ReminderDialog = ({ event, onClose, onSetReminder }) => (
  <motion.div
    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onClose}
  >
    <motion.div
      className="bg-gradient-to-br from-sec-dark to-pri-dark p-6 rounded-2xl max-w-sm w-full mx-4 border border-pri-white/10"
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      onClick={e => e.stopPropagation()}
    >
      <h3 className="text-xl font-bold text-pri-yellow mb-4">Set Reminder</h3>
      {/* Add reminder options here */}
      <div className="flex justify-end mt-4">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onClose}
          className="px-4 py-2 rounded-xl bg-pri-white/10 text-pri-white hover:bg-pri-white/20"
        >
          Close
        </motion.button>
      </div>
    </motion.div>
  </motion.div>
);

const EventDialog = ({ event, onClose, onDelete, onEdit, onShare, onSetReminder }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedEvent, setEditedEvent] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [duration, setDuration] = useState({ hours: 0, minutes: 0 });

  useEffect(() => {
    if (event) {
      const durationMinutes = differenceInMinutes(parseISO(event.endTime), parseISO(event.startTime));
      setDuration({
        hours: Math.floor(durationMinutes / 60),
        minutes: durationMinutes % 60
      });
      setEditedEvent(event);
    }
  }, [event]);

  const handleDurationChange = (type, value) => {
    const newDuration = {
      ...duration,
      [type]: parseInt(value) || 0
    };
    setDuration(newDuration);

    if (editedEvent) {
      const startTime = parseISO(editedEvent.startTime);
      const totalMinutes = (newDuration.hours * 60) + newDuration.minutes;
      const newEndTime = addMinutes(startTime, totalMinutes);
      setEditedEvent({
        ...editedEvent,
        endTime: newEndTime.toISOString()
      });
    }
  };

  const handleStartTimeChange = (newStartTime) => {
    const totalMinutes = (duration.hours * 60) + duration.minutes;
    const newEndTime = addMinutes(parseISO(newStartTime), totalMinutes);
    setEditedEvent({
      ...editedEvent,
      startTime: newStartTime,
      endTime: newEndTime.toISOString()
    });
  };

  if (!event) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-pri-dark/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-6"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", damping: 25 }}
          className="relative w-full max-w-lg"
          onClick={e => e.stopPropagation()}
        >
          {/* Glossy Card */}
          <div className="relative bg-gradient-to-b from-sec-dark/90 to-pri-dark/90 rounded-2xl shadow-2xl overflow-hidden">
            {/* Glossy Effects */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-br from-pri-yellow/5 via-transparent to-pri-green/5" />
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-pri-white/5 to-transparent" />
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-pri-yellow/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-pri-white/10 to-transparent" />
            </div>

            {/* Header */}
            <div className="relative px-6 py-4 border-b border-pri-white/10">
              <div className="flex justify-between items-center">
                <motion.h2 
                  className="text-2xl font-bold bg-gradient-to-r from-pri-yellow to-pri-green bg-clip-text text-transparent"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  {event.title}
                </motion.h2>
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  className="text-pri-gray hover:text-pri-white transition-colors"
                  onClick={onClose}
                >
                  <XMarkIcon className="h-6 w-6" />
                </motion.button>
              </div>
            </div>

            {/* Content */}
            {isEditing ? (
              <div className="relative px-6 py-4 space-y-4">
                {/* Title Input */}
                <input
                  type="text"
                  value={editedEvent.title}
                  onChange={e => setEditedEvent({...editedEvent, title: e.target.value})}
                  className="w-full px-4 py-2 bg-pri-dark/50 rounded-xl text-pri-white border border-pri-white/10"
                  placeholder="Event Title"
                />

                {/* Start Time and Duration Section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-pri-yellow/70">Start Time</label>
                    <input
                      type="datetime-local"
                      value={format(parseISO(editedEvent.startTime), "yyyy-MM-dd'T'HH:mm")}
                      onChange={e => handleStartTimeChange(e.target.value)}
                      className="w-full px-4 py-2 bg-pri-dark/50 rounded-xl text-pri-white border border-pri-white/10"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-pri-yellow/70">Duration</label>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max="23"
                          value={duration.hours}
                          onChange={e => handleDurationChange('hours', e.target.value)}
                          className="w-full px-4 py-2 bg-pri-dark/50 rounded-xl text-pri-white border border-pri-white/10"
                          placeholder="Hours"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-pri-white/50 text-sm">hrs</span>
                      </div>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max="59"
                          value={duration.minutes}
                          onChange={e => handleDurationChange('minutes', e.target.value)}
                          className="w-full px-4 py-2 bg-pri-dark/50 rounded-xl text-pri-white border border-pri-white/10"
                          placeholder="Minutes"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-pri-white/50 text-sm">min</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* End Time Display */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-pri-yellow/70">End Time</label>
                  <div className="px-4 py-2 bg-pri-dark/30 rounded-xl text-pri-white/70">
                    {format(parseISO(editedEvent.endTime), "MMM d, yyyy h:mm a")}
                  </div>
                </div>

                {/* Location Input */}
                <input
                  type="text"
                  value={editedEvent.location || ''}
                  onChange={e => setEditedEvent({...editedEvent, location: e.target.value})}
                  placeholder="Location"
                  className="w-full px-4 py-2 bg-pri-dark/50 rounded-xl text-pri-white border border-pri-white/10"
                />

                {/* Description Input */}
                <textarea
                  value={editedEvent.description || ''}
                  onChange={e => setEditedEvent({...editedEvent, description: e.target.value})}
                  placeholder="Description"
                  className="w-full px-4 py-2 bg-pri-dark/50 rounded-xl text-pri-white border border-pri-white/10 resize-none"
                  rows={3}
                />

                {/* Action Buttons */}
                <div className="flex justify-end space-x-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 rounded-xl bg-pri-dark/50 text-pri-white"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      onEdit(editedEvent);
                      setIsEditing(false);
                    }}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-pri-yellow to-pri-green text-pri-dark"
                  >
                    Save
                  </motion.button>
                </div>
              </div>
            ) : (
              <div className="relative px-6 py-4 space-y-4">
                {/* Time Section */}
                <motion.div 
                  className="flex items-center space-x-3 text-pri-white"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="p-2 rounded-xl bg-gradient-to-br from-pri-yellow/20 to-pri-green/20 backdrop-blur-sm">
                    <FaClock className="h-5 w-5 text-pri-yellow" />
                  </div>
                  <div>
                    <p className="font-medium">{format(parseISO(event.startTime), 'EEEE, MMMM d, yyyy')}</p>
                    <p className="text-pri-gray">
                      {format(parseISO(event.startTime), 'h:mm a')} - {format(parseISO(event.endTime), 'h:mm a')}
                    </p>
                  </div>
                </motion.div>

                {/* Location Section */}
                {event.location && (
                  <motion.div 
                    className="flex items-center space-x-3 text-pri-white"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <div className="p-2 rounded-xl bg-gradient-to-br from-pri-yellow/20 to-pri-green/20 backdrop-blur-sm">
                      <FaMapMarkerAlt className="h-5 w-5 text-pri-green" />
                    </div>
                    <p className="font-medium">{event.location}</p>
                  </motion.div>
                )}

                {/* Description Section */}
                {event.description && (
                  <motion.div 
                    className="mt-4 text-pri-white/90"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="p-4 rounded-xl bg-gradient-to-br from-pri-yellow/10 to-pri-green/10 backdrop-blur-sm border border-pri-white/10">
                      <p className="text-sm leading-relaxed">{event.description}</p>
                    </div>
                  </motion.div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="relative px-6 py-4 border-t border-pri-white/10 bg-gradient-to-r from-pri-yellow/5 to-pri-green/5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsEditing(true)}
                  className="flex items-center justify-center space-x-2 px-3 py-2 rounded-xl bg-gradient-to-br from-pri-yellow/20 to-pri-green/20 hover:from-pri-yellow/30 hover:to-pri-green/30 backdrop-blur-sm text-pri-white transition-all duration-300"
                >
                  <FaPencilAlt className="h-4 w-4" />
                  <span className="hidden sm:inline">Edit</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center justify-center space-x-2 px-3 py-2 rounded-xl bg-gradient-to-br from-red-500/20 to-red-600/20 hover:from-red-500/30 hover:to-red-600/30 backdrop-blur-sm text-red-400 transition-all duration-300"
                >
                  <FaTrash className="h-4 w-4" />
                  <span className="hidden sm:inline">Delete</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onSetReminder(event)}
                  className="flex items-center justify-center space-x-2 px-3 py-2 rounded-xl bg-gradient-to-br from-pri-yellow/20 to-pri-green/20 hover:from-pri-yellow/30 hover:to-pri-green/30 backdrop-blur-sm text-pri-white transition-all duration-300"
                >
                  <FaBell className="h-4 w-4" />
                  <span className="hidden sm:inline">Remind</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onShare(event)}
                  className="flex items-center justify-center space-x-2 px-3 py-2 rounded-xl bg-gradient-to-br from-pri-yellow/20 to-pri-green/20 hover:from-pri-yellow/30 hover:to-pri-green/30 backdrop-blur-sm text-pri-white transition-all duration-300"
                >
                  <FaShare className="h-4 w-4" />
                  <span className="hidden sm:inline">Share</span>
                </motion.button>
              </div>
            </div>

            {/* Delete Confirmation Dialog */}
            <AnimatePresence>
              {showDeleteConfirm && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 bg-pri-dark/90 backdrop-blur-md rounded-2xl flex items-center justify-center overflow-hidden"
                >
                  {/* Glossy Background Effects */}
                  <div className="absolute inset-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-pri-yellow/5 via-transparent to-pri-green/5" />
                    <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent" />
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-black/10 to-transparent" />
                  </div>

                  {/* Content */}
                  <motion.div 
                    initial={{ scale: 0.9, y: 20 }}
                    animate={{ scale: 1, y: 0 }}
                    exit={{ scale: 0.9, y: 20 }}
                    className="relative p-6 text-center max-w-sm mx-4"
                  >
                    <div className="space-y-4">
                      {/* Warning Icon */}
                      <div className="flex justify-center">
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", delay: 0.1 }}
                          className="w-16 h-16 rounded-full bg-gradient-to-br from-red-500/20 to-red-600/20 flex items-center justify-center"
                        >
                          <FaTrash className="h-8 w-8 text-red-500" />
                        </motion.div>
                      </div>

                      {/* Text Content */}
                      <div className="space-y-2">
                        <h3 className="text-xl font-bold bg-gradient-to-r from-pri-yellow to-pri-green bg-clip-text text-transparent">
                          Delete Event?
                        </h3>
                        <p className="text-pri-gray text-sm">
                          Are you sure you want to delete "{event.title}"? This action cannot be undone.
                        </p>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex justify-center space-x-3">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setShowDeleteConfirm(false)}
                          className="px-4 py-2 rounded-xl bg-gradient-to-br from-pri-white/10 to-pri-white/5 backdrop-blur-sm text-pri-white border border-pri-white/10 hover:border-pri-white/20 transition-all duration-300"
                        >
                          Cancel
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            onDelete(event.id);
                            setShowDeleteConfirm(false);
                          }}
                          className="px-4 py-2 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg hover:shadow-red-500/25 transition-all duration-300"
                        >
                          Delete
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default function WeekView({ showSharedEvents = true }) {
  const dispatch = useDispatch();
  const selectedDateString = useSelector((state) => state.calendar.selectedDate);
  const selectedDate = useMemo(() => new Date(selectedDateString), [selectedDateString]);
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const [events, setEvents] = useState([]);
  const sharedEvents = useSelector((state) => state.calendar.sharedEvents || []);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState('');
  const isLoadingShared = useSelector((state) => state.calendar.isLoadingShared);
  const sharedEventsError = useSelector((state) => state.calendar.sharedEventsError);
  const pendingShareRequests = useSelector((state) => 
    state.calendar.shareRequests?.filter(req => 
      req.status === 'pending' && req.recipientId === user?.id
    ) || []
  );
  
  // Refs for tracking fetch status
  const fetchStatusRef = useRef({
    weekStart: null,
    userId: null,
    sharedFetched: false
  });
  
  // Ref for scrolling content
  const mainContentRef = useRef(null);
  const timeLabelsRef = useRef(null);
  
  // Memoize date-related values for better performance
  const weekStart = useMemo(() => startOfWeek(selectedDate, { weekStartsOn: 1 }), [selectedDate]);
  const weekEnd = useMemo(() => endOfWeek(selectedDate, { weekStartsOn: 1 }), [selectedDate]);
  const daysInWeek = useMemo(() => 
    eachDayOfInterval({ start: weekStart, end: weekEnd }), 
    [weekStart, weekEnd]
  );
  
  // Generate time slots for 24 hours
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let i = 0; i < 24; i++) {
      // Add the hour
      const hourDate = new Date();
      hourDate.setHours(i, 0, 0, 0);
      slots.push({ time: hourDate, isHour: true });
      
      // Add the 30-minute mark
      const halfHourDate = new Date();
      halfHourDate.setHours(i, 30, 0, 0);
      slots.push({ time: halfHourDate, isHour: false });
    }
    return slots;
  }, []);

  // Fetch events for the current week
  const fetchEvents = useCallback(async () => {
    if (!user?.id) return;
    
    const weekStartStr = format(weekStart, 'yyyy-MM-dd');
    
    // Skip if we've already fetched for this week and user
    if (fetchStatusRef.current.weekStart === weekStartStr && 
        fetchStatusRef.current.userId === user.id) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await calendarService.fetchWeekEvents(weekStartStr, user.id);
      const eventsArray = response.events || [];
      setEvents(eventsArray);
      
      // Update fetch status
      fetchStatusRef.current.weekStart = weekStartStr;
      fetchStatusRef.current.userId = user.id;
    } catch (error) {
      setError('Failed to load events. Please try again.');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, weekStart]);

  // Fetch shared events
  const fetchSharedEvents = useCallback(async () => {
    if (!user?.id) return;
    
    // Skip if we've already fetched shared events for this user
    if (fetchStatusRef.current.sharedFetched && 
        fetchStatusRef.current.userId === user.id) {
      return;
    }
    
    try {
      dispatch(setLoadingShared(true));
      const response = await calendarService.fetchSharedEvents(user.id);
      const sharedEventsData = response.events || [];
      dispatch(setSharedEvents(sharedEventsData));
      
      // Update fetch status
      fetchStatusRef.current.sharedFetched = true;
      fetchStatusRef.current.userId = user.id;
    } catch (error) {
      dispatch(setSharedEventsError('Failed to load shared events'));
    } finally {
      dispatch(setLoadingShared(false));
    }
  }, [user?.id, dispatch]);

  // Reset fetch status when week changes
  useEffect(() => {
    fetchStatusRef.current.weekStart = null;
  }, [weekStart]);

  // Reset shared events fetch status when user changes
  useEffect(() => {
    if (user?.id !== fetchStatusRef.current.userId) {
      fetchStatusRef.current.sharedFetched = false;
    }
  }, [user?.id]);

  // Effect to fetch events on mount or when the week changes
  useEffect(() => {
    if (user?.id) {
      fetchEvents();
    }
  }, [fetchEvents]);

  // Effect to fetch shared events on mount or when user changes
  useEffect(() => {
    if (user?.id) {
      fetchSharedEvents();
    }
  }, [fetchSharedEvents]);

  // Synchronize scrolling between time labels and main content
  useEffect(() => {
    const timeLabelsContainer = timeLabelsRef.current;
    const mainContentContainer = mainContentRef.current;

    if (timeLabelsContainer && mainContentContainer) {
      const syncScroll = () => {
        timeLabelsContainer.scrollTop = mainContentContainer.scrollTop;
      };

      mainContentContainer.addEventListener('scroll', syncScroll);

      return () => {
        mainContentContainer.removeEventListener('scroll', syncScroll);
      };
    }
  }, []);

  // Scroll to current time on initial load
  useEffect(() => {
    if (mainContentRef.current) {
      const now = new Date();
      const currentHour = now.getHours();
      const scrollPosition = (currentHour * 60) - 120; // Scroll to current hour minus 2 hours
      mainContentRef.current.scrollTop = Math.max(0, scrollPosition);
    }
  }, []);

  // Event handlers
  const handleUpdateEvent = useCallback(async (updatedEvent) => {
    if (!updatedEvent?.id) return;
    
    setLoading(true);
    setFeedback('');
    
    try {
      const result = await calendarService.updateEvent(updatedEvent);
      
      // Update local state
      setEvents(prev => prev.map(event => 
        event.id === updatedEvent.id ? result : event
      ));
      
      // Show success message
      setFeedback('Event updated successfully');
      setTimeout(() => setFeedback(''), 3000);
    } catch (error) {
      setFeedback('Failed to update event');
      setTimeout(() => setFeedback(''), 3000);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDeleteEvent = useCallback(async (eventId) => {
    if (!eventId) return;
    
    setLoading(true);
    setFeedback('');
    
    try {
      await calendarService.deleteEvent(eventId);
      
      // Update local state
      setEvents(prev => prev.filter(event => event.id !== eventId));
      
      // Close event details if needed
      if (selectedEvent && selectedEvent.id === eventId) {
        setSelectedEvent(null);
      }
      
      // Show success message
      setFeedback('Event deleted successfully');
      setTimeout(() => setFeedback(''), 3000);
    } catch (error) {
      setFeedback('Failed to delete event');
      setTimeout(() => setFeedback(''), 3000);
    } finally {
      setLoading(false);
    }
  }, [selectedEvent]);

  const handleSetReminder = async (event, reminderTime) => {
    try {
      await dispatch(setReminder({ eventId: event.id, reminderTime }));
    } catch (error) {
      // Handle error silently
    }
  };

  const [editingEvent, setEditingEvent] = useState(null);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [eventToShare, setEventToShare] = useState(null);

  const handleShareEvent = async (event) => {
    try {
      // Open the share dialog
      setEventToShare(event);
      setIsShareDialogOpen(true);
    } catch (error) {
      // Handle error silently
      setFeedback('Failed to share event. Please try again.');
      setTimeout(() => setFeedback(''), 3000);
    }
  };

  // Combine user's events and shared events with deduplication
  const allEvents = useMemo(() => {
    // Get all pendingEventIds so we don't show them
    const pendingEventIds = pendingShareRequests.map(req => req.eventId);
    
    // Start with processing our own events array to identify which ones are actually shared
    const processedOwnEvents = events.map(event => {
      // If the owner ID is different from the current user, it's a shared event
      if (event.owner && event.owner.id && event.owner.id !== user?.id) {
        return { ...event, isShared: true };
      }

      // If userId doesn't match current user, it's a shared event
      if (event.userId && event.userId !== user?.id) {
        return { ...event, isShared: true };
      }
      
      // If the event is in sharedWith for this user, it's a shared event
      if (event.sharedWith && Array.isArray(event.sharedWith) && event.sharedWith.length > 0) {
        // If we find the current user in sharedWith, and they don't own the event
        const isSharedWithUser = event.sharedWith.some(share => 
          share.userId === user?.id || (share.user && share.user.id === user?.id)
        );
        
        if (isSharedWithUser) {
          return { ...event, isShared: true };
        }
      }
      
      return event;
    });
    
    // Filter out shared events if toggle is off
    let combinedEvents = showSharedEvents 
      ? processedOwnEvents 
      : processedOwnEvents.filter(event => !event.isShared);
    
    // Only add additional shared events if the toggle is on
    if (showSharedEvents) {
      // Process shared events array
      const processedSharedEvents = sharedEvents
        .filter(event => {
          // Skip if this is a pending event
          if (pendingEventIds.includes(event.id)) return false;
          
          // Skip if we already have this event
          if (combinedEvents.some(e => e.id === event.id)) return false;
          
          // Skip if this is actually owned by the current user
          if (event.userId === user?.id) return false;
          if (event.owner && event.owner.id === user?.id) return false;
          
          return true;
        })
        .map(event => ({ ...event, isShared: true }));
      
      // Add filtered shared events to the combined list
      combinedEvents = [...combinedEvents, ...processedSharedEvents];
    }
    
    return combinedEvents;
  }, [events, sharedEvents, showSharedEvents, pendingShareRequests, user?.id]);

  // Filter events for a specific day
  const getEventsForDay = useCallback((day) => {
    return allEvents.filter(event => {
      const startTime = event.startTime || event.start;
      if (!startTime) return false;
      
      return isSameDay(parseISO(startTime), day);
    });
  }, [allEvents]);

  // Render events for a specific day
  const renderEvents = (day) => {
    const dayEvents = getEventsForDay(day);
    
    return dayEvents.map((event) => {
      const startTime = parseISO(event.startTime || event.start);
      const endTime = parseISO(event.endTime || event.end);
      const startMinutes = startTime.getHours() * 60 + startTime.getMinutes();
      const duration = differenceInMinutes(endTime, startTime);
      const topPosition = (startMinutes / 30) * 30;
      const height = (duration / 30) * 30;

      // Determine background style based on event color and shared status
      const getEventBackground = () => {
        const defaultGradient = 'linear-gradient(to right, var(--soft-pink), var(--mint-green))';
        
        if (event.isShared) {
          // For shared events, add a striped pattern
          if (event.color) {
            return {
              background: event.color,
              backgroundImage: `linear-gradient(45deg, rgba(255,255,255,0.1) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.1) 75%, transparent 75%, transparent)`,
              backgroundSize: '8px 8px'
            };
          } else {
            return {
              background: defaultGradient,
              backgroundImage: `linear-gradient(45deg, rgba(255,255,255,0.1) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.1) 75%, transparent 75%, transparent), ${defaultGradient}`,
              backgroundSize: '8px 8px, 100%'
            };
          }
        } else {
          // For regular events, use the color directly if available
          if (event.color) {
            return { background: event.color };
          } else {
            return { background: defaultGradient };
          }
        }
      };

      const backgroundStyle = getEventBackground();

      return (
        <motion.div
          key={event.id}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          whileHover={{ scale: 1.02 }}
          className={`absolute left-1 right-1 rounded-lg shadow-lg backdrop-blur-sm overflow-hidden cursor-pointer ${
            event.isShared ? 'border border-pri-yellow/50' : ''
          }`}
          style={{
            top: `${topPosition}px`,
            height: `${height}px`,
            ...backgroundStyle
          }}
          onClick={() => setSelectedEvent(event)}
        >
          <div className="p-2 h-full group relative">
            {/* Glossy effects */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent" />
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
            </div>
            
            <div className="flex items-center space-x-1">
              <FaClock className="h-3 w-3 text-pri-dark/80 flex-shrink-0" />
              <span className="text-xs text-pri-dark font-medium">
                {format(startTime, 'h:mm a')}
              </span>
              {event.isShared && <FaUserFriends className="ml-1 h-2.5 w-2.5 text-pri-dark/80 flex-shrink-0" />}
            </div>
            <div className="font-medium text-pri-dark text-sm mt-0.5 line-clamp-2 drop-shadow-sm">
              {event.title}
            </div>
            
            {/* Duration Badge - only shows on hover */}
            {height > 60 && (
              <div className="absolute bottom-1 right-1 text-[10px] bg-pri-dark/10 px-1.5 py-0.5 rounded-full text-pri-dark/80
                        opacity-0 group-hover:opacity-100 transition-opacity duration-300 border border-pri-dark/10">
                {(() => {
                  const mins = differenceInMinutes(endTime, startTime);
                  const hours = Math.floor(mins / 60);
                  const remainingMins = mins % 60;
                  return hours > 0 ? `${hours}h ${remainingMins}m` : `${remainingMins}m`;
                })()}
              </div>
            )}
            
            {/* Hover Effect Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/5 via-transparent to-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
        </motion.div>
      );
    });
  };

  // Render timeline marker for current time
  const renderCurrentTimeIndicator = () => {
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const currentPercentage = (currentMinutes / (24 * 60)) * timeSlots.length * 30;
    
    return (
      <div 
        className="absolute left-0 right-0 flex items-center z-20"
        style={{
          top: `${currentPercentage}px`,
        }}
      >
        <div className="w-2 h-2 rounded-full bg-red-500 shadow-lg shadow-red-500/50" />
        <div className="flex-1 border-t border-dashed border-red-500" />
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-pri-dark via-[#1a1c2e] to-sec-dark text-pri-white">
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* Days of Week Header */}
        <div className="sticky top-0 z-20 bg-gradient-to-b from-pri-dark/95 to-pri-dark/80 backdrop-blur-xl border-b border-pri-white/10">
          <div className="grid grid-cols-8" style={{ gridTemplateColumns: '60px repeat(7, 1fr)' }}>
            {/* Empty cell for time column header */}
            <div className="border-b border-pri-white/10"></div>
            
            {daysInWeek.map((day) => (
              <motion.div
                key={day.toISOString()}
                className={`py-2 flex flex-col items-center ${
                  isToday(day) ? 'bg-pri-yellow/10' : ''
                } cursor-pointer hover:bg-pri-white/5 transition-colors border-b border-l border-pri-white/10`}
                onClick={() => dispatch(setSelectedDate({ selectedDate: day.toString() }))}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="text-xs font-medium text-pri-gray">
                  {format(day, 'EEE')}
                </span>
                <span
                  className={`text-sm rounded-full h-6 w-6 flex items-center justify-center ${
                    isSameDay(day, selectedDate)
                      ? 'bg-pri-yellow text-pri-dark font-bold'
                      : isToday(day)
                      ? 'text-pri-yellow font-medium'
                      : 'text-pri-white'
                  }`}
                >
                  {format(day, 'd')}
                </span>
                <span className="text-xs text-pri-white/50">
                  {format(day, 'MMM')}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* Week Time Grid */}
        <div className="flex-1 flex overflow-hidden">
          {/* Time Labels Column */}
          <div className="w-[60px] flex-shrink-0 bg-gradient-to-b from-sec-dark/80 to-pri-dark/80 backdrop-blur-xl sticky left-0 z-10">
            <div className="h-full overflow-y-scroll hide-scrollbar" ref={timeLabelsRef}>
              {timeSlots.map((slot, index) => (
                <div 
                  key={index} 
                  className="h-[30px] relative flex items-start justify-end pr-2"
                >
                  {slot.isHour && (
                    <div className="text-xs text-pri-gray font-medium absolute top-0 right-2 -translate-y-1/2">
                      {format(slot.time, 'h a')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Main Calendar Grid */}
          <div className="flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto hide-scrollbar relative" ref={mainContentRef}>
              <div className="grid grid-cols-7 min-h-[1440px] border-l border-pri-white/10" style={{ height: `${timeSlots.length * 30}px` }}>
                {daysInWeek.map((day) => (
                  <div
                    key={day.toISOString()}
                    className={`border-r border-pri-white/10 relative ${
                      isToday(day) ? 'bg-pri-yellow/5' : ''
                    }`}
                  >
                    {/* Time indicators */}
                    {timeSlots.map((slot, index) => (
                      <div
                        key={index}
                        className={`absolute w-full ${
                          slot.isHour
                            ? 'border-b border-dashed border-pri-white/5' 
                            : 'border-b border-pri-white/10'
                        }`}
                        style={{ top: `${index * 30}px`, height: '30px' }}
                      />
                    ))}
                    
                    {/* Current time indicator (only for today) */}
                    {isToday(day) && renderCurrentTimeIndicator()}
                    
                    {/* Events for this day */}
                    {renderEvents(day)}
                    
                    {/* Glossy overlay */}
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute inset-0 bg-gradient-to-b from-pri-white/5 to-transparent opacity-20" style={{ height: '5%' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Event Details Dialog */}
      <AnimatePresence>
        {selectedEvent && (
          <EventDetailsDialog
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
            onDelete={handleDeleteEvent}
            onEdit={handleUpdateEvent}
            onShare={handleShareEvent}
            onSetReminder={handleSetReminder}
            isShared={selectedEvent.userId !== user?.id || selectedEvent.isShared || false}
          />
        )}
      </AnimatePresence>
      
      {/* Loading Indicator */}
      <AnimatePresence>
        {loading && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed top-4 right-4 bg-gradient-to-r from-pri-yellow to-pri-green px-3 py-1.5 rounded-lg text-pri-dark text-sm font-medium shadow-lg"
          >
            Loading...
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Feedback Toast */}
      <AnimatePresence>
        {feedback && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-pri-yellow text-pri-dark px-4 py-2 rounded-xl shadow-lg"
          >
            {feedback}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add the ShareEventDialog component */}
      <ShareEventDialog
        isOpen={isShareDialogOpen}
        onClose={() => setIsShareDialogOpen(false)}
        event={eventToShare}
        onEventShared={() => {
          setFeedback('Event shared successfully!');
          setTimeout(() => setFeedback(''), 3000);
          
          // Refresh events to get the updated sharing status
          if (user?.id) {
            const dateStr = format(selectedDate, 'yyyy-MM-dd');
            dispatch(fetchWeekEvents({ date: dateStr, userId: user.id }));
          }
        }}
      />
    </div>
  );
}
